import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX, ArrowRight } from "lucide-react";
import { useAudio } from "@/context/AudioContext";

// Cinematic intro gate. Blocks the experience until the visitor chooses to enter.
// Entering with sound satisfies the browser's user-gesture requirement to start audio.
export const IntroGate = ({ onEnter }) => {
  const { enableSound } = useAudio();
  const reduce = useReducedMotion();
  const [leaving, setLeaving] = useState(false);

  const handleEnter = async (withSound) => {
    if (withSound) {
      await enableSound();
    }
    setLeaving(true);
    window.setTimeout(() => onEnter(), reduce ? 0 : 900);
  };

  return (
    <AnimatePresence>
      {!leaving && (
        <motion.div
          key="intro-gate"
          data-testid="intro-gate"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-[color:var(--bp-bg)] hero-noise"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* faint orbiting rings backdrop */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 0.5, scale: 1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          >
            <svg width="680" height="680" viewBox="0 0 680 680" className="max-w-[92vw]">
              <circle cx="340" cy="340" r="250" fill="none" stroke="#144970" strokeWidth="1" opacity="0.5" />
              <circle cx="340" cy="340" r="180" fill="none" stroke="#2DB5A5" strokeWidth="0.8" opacity="0.35" />
              <circle cx="340" cy="340" r="120" fill="none" stroke="#D4AF37" strokeWidth="0.6" opacity="0.25" />
            </svg>
          </motion.div>

          <div className="relative z-10 flex flex-col items-center px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.82 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex items-center justify-center"
            >
              <img
                src={`${process.env.PUBLIC_URL || ""}/brand/logo.png`}
                alt="Beyond Potentials"
                draggable={false}
                className="h-28 w-28 select-none drop-shadow-[0_10px_40px_rgba(45,181,165,0.25)] sm:h-32 sm:w-32"
              />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mt-8 text-[11px] font-semibold uppercase tracking-[0.32em] text-[color:var(--bp-teal)]"
            >
              By bankers, for bankers
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-5 max-w-md font-display text-xl font-medium leading-snug text-[color:var(--bp-cream)] sm:text-2xl"
            >
              Executive search &amp; talent intelligence for Monaco &amp; Geneva private banking.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9 }}
              className="mt-11 flex flex-col items-center gap-4 sm:flex-row"
            >
              <button
                type="button"
                data-testid="intro-enter-button"
                onClick={() => handleEnter(true)}
                className="group inline-flex items-center gap-3 rounded-full bg-[color:var(--bp-teal)] px-8 py-4 text-sm font-semibold text-[color:var(--bp-bg)] shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-lift)]"
              >
                <Volume2 className="h-4 w-4" />
                Enter with sound
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                data-testid="intro-enter-silent-button"
                onClick={() => handleEnter(false)}
                className="inline-flex items-center gap-2 rounded-full border border-[color:var(--bp-border)] px-6 py-4 text-sm font-medium text-[color:var(--bp-text-muted)] transition-colors hover:border-[color:var(--bp-teal)] hover:text-[color:var(--bp-cream)]"
              >
                <VolumeX className="h-4 w-4" />
                Enter in silence
              </button>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 1 }}
              className="mt-7 text-[11px] uppercase tracking-[0.2em] text-[color:var(--bp-text-muted)]/70"
            >
              Best experienced with sound
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
